import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import entityElementService from "services/entity-elements-service";
import useOpen from "utils/useOpen";
import EntityElementsDialog from "./EntityElementsDialog";

export default function AllEntityElements({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [entityElements, setEntityElements] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddEntityElement(newElement) {
    const [response, data] =
      await entityElementService.createEntityElement(newElement);
    if (response.ok) {
      const [response, data] =
        await entityElementService.getEntityElements(lastFilter);
      if (response.ok) {
        setEntityElements(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateEntityElement(newElement) {
    const [response, data] = await entityElementService.updateEntityElement(
      newElement.id,
      {
        ...newElement,
        options: newElement.options
          ? newElement.options.map((option) => {
              return {
                name: option.label,
                sequence: option.sequence,
                value: option.id,
              };
            })
          : null,
      },
    );
    if (response.ok) {
      const [response, data] =
        await entityElementService.getEntityElements(lastFilter);
      if (response.ok) {
        setEntityElements(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteEntityElement(id) {
    const [response, data] = await entityElementService.removeEntityElement(id);
    if (response.ok) {
      const [response, data] =
        await entityElementService.getEntityElements(lastFilter);
      if (response.ok) {
        setEntityElements(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!entityElements?.length)
      return entityElements?.length === 0
        ? [
            {
              name: label("no-entity-elements-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return entityElements.map((entityElement) => {
      return {
        name: (
          <MDTypography variant="inherit">{entityElement.name}</MDTypography>
        ),
        referenceCode: (
          <MDTypography variant="inherit">
            {entityElement.referenceCode || label("none")}
          </MDTypography>
        ),
        entity: (
          <MDTypography variant="inherit">
            {entityElement.entity?.name || "—"}
          </MDTypography>
        ),
        serialNumber: (
          <MDTypography variant="inherit">
            {entityElement.serialNumber || "—"}
          </MDTypography>
        ),
        account: (
          <MDTypography variant="inherit">
            {entityElement.account?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!entityElement.accountId && can("isAdmin")) ||
          entityElement.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        entityElement,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        entityElement,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [entityElements, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("reference-code-singular"),
        accessor: "referenceCode",
      },
      {
        Header: label("entity-singular"),
        accessor: "entity",
      },
      {
        Header: "Serial Number",
        accessor: "serialNumber",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteEntityElement(open.get("alert").entityElement.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <EntityElementsDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddEntityElement}
          onClose={() => open.change("create", false)}
          data={null}
        />
      ) : null}
      {open.get("update") ? (
        <EntityElementsDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateEntityElement}
          onClose={() => open.change("update", false)}
          data={open.get("update").entityElement}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("entity-element-plural")}{" "}
                  {permissions.get ? `(${entityElements?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-entity-element-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-entityElements"
                      internalLoading={!Boolean(entityElements)}
                      fullWidth
                      setData={setEntityElements}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(
                          await entityElementService.getEntityElements(request),
                        );
                      }}
                      label={label("search-entity-elements-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(entityElements)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
